import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import {
  normalizeSearchSource,
  resolveSearchSourceConnectionStatus,
  type SearchSourceConnectionStatus,
} from '../utils/sourceConnectionStatus';

interface SearchFromSourcesHeaderProps {
  onHHSettingsClick?: () => void;
  onHHAuthClick?: () => void;
  onATSSettingsClick?: () => void;
  tabToggle?: React.ReactNode;
  className?: string;
  /** Текущий источник поиска ('hh' | 'estaff' | 'skillaz' | 'avito') */
  source?: string;
}

const SearchFromSourcesHeader: React.FC<SearchFromSourcesHeaderProps> = ({
  onHHSettingsClick,
  onHHAuthClick,
  onATSSettingsClick,
  tabToggle,
  className = '',
  source = 'hh',
}) => {
  const normalizedSource = normalizeSearchSource(source);
  const isHHSource = normalizedSource === 'hh';
  const [connectionStatus, setConnectionStatus] = useState<SearchSourceConnectionStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const { isAuth, isLoading: authLoading, token } = useAuthStore();

  useEffect(() => {
    let cancelled = false;
    const delay = (ms: number) => new Promise(res => setTimeout(res, ms));
    const fetchStatus = async (attempt: number = 1) => {
      if (authLoading || !isAuth || !token) {
        setConnectionStatus(null);
        setStatusLoading(false);
        return;
      }

      setStatusLoading(true);
      try {
        const status = await resolveSearchSourceConnectionStatus(normalizedSource);
        if (!cancelled) setConnectionStatus(status);
      } catch {
        if (!cancelled) setConnectionStatus('unavailable');
        if (!cancelled && attempt < 2) {
          await delay(400);
          if (!cancelled) await fetchStatus(attempt + 1);
        }
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };

    setConnectionStatus(null);
    void fetchStatus();
    return () => { cancelled = true; };
  }, [authLoading, isAuth, token, normalizedSource]);

  const statusTone =
    connectionStatus === 'connected'
      ? 'green'
      : connectionStatus === 'auth-required' || connectionStatus === 'ats-required'
        ? 'yellow'
        : 'gray';

  const statusText = (() => {
    if (authLoading) return 'Загрузка...';
    if (statusLoading) return 'Проверка статуса…';
    if (connectionStatus === 'connected') {
      return isHHSource ? 'HH.ru подключен' : 'ATS подключена';
    }
    if (connectionStatus === 'auth-required') return 'Требуется авторизация';
    if (connectionStatus === 'ats-required') return 'Требуется настройка ATS';
    return isHHSource ? 'HH статус недоступен' : 'Статус недоступен';
  })();

  const actionText = (() => {
    if (connectionStatus === 'auth-required' && isHHSource) return 'Авторизоваться в HH.ru';
    if (connectionStatus === 'ats-required') return 'Настроить ATS';
    if (isHHSource) return 'Настройки HH.ru';
    return connectionStatus === 'connected' ? 'Настройки ATS' : 'Настроить ATS';
  })();

  const handleActionClick = () => {
    if (connectionStatus === 'auth-required' && isHHSource) {
      (onHHAuthClick ?? onHHSettingsClick)?.();
      return;
    }

    if (connectionStatus === 'ats-required') {
      (onATSSettingsClick ?? onHHSettingsClick ?? onHHAuthClick)?.();
      return;
    }

    if (isHHSource) {
      onHHSettingsClick?.();
      return;
    }

    (onATSSettingsClick ?? onHHSettingsClick)?.();
  };

  return (
    <div className={`flex items-center gap-4 w-full ${className}`}>
      <h1 className="text-2xl font-semibold text-[#0a0d14] shrink-0">Поиск из источников</h1>
      {tabToggle}
      <div className="flex gap-2 items-center shrink-0 ml-auto">
        <div
          className={`h-9 relative rounded-lg shrink-0 px-3 py-0 flex items-center ${
            statusTone === 'green'
              ? 'bg-green-50'
              : statusTone === 'yellow'
                ? 'bg-yellow-50'
                : 'bg-gray-50'
          }`}
        >
          <div
            aria-hidden="true"
            className={`absolute border border-solid inset-0 pointer-events-none rounded-lg ${
              statusTone === 'green'
                ? 'border-green-200'
                : statusTone === 'yellow'
                  ? 'border-yellow-200'
                  : 'border-gray-200'
            }`}
          />
          <div className="flex items-center h-full gap-2 relative">
            <div
              className={`w-4 h-4 rounded-full shrink-0 ${
                statusTone === 'green'
                  ? 'bg-green-500'
                  : statusTone === 'yellow'
                    ? 'bg-yellow-500'
                    : 'bg-gray-400'
              }`}
            />
            <span
              className={`text-sm font-normal leading-5 ${
                statusTone === 'green'
                  ? 'text-green-800'
                  : statusTone === 'yellow'
                    ? 'text-yellow-800'
                    : 'text-gray-600'
              }`}
            >
              {statusText}
            </span>
          </div>
        </div>
        <button
          onClick={handleActionClick}
          className="bg-gray-100 h-9 rounded-lg shrink-0 px-3 py-0 hover:bg-gray-200 transition-colors cursor-pointer flex items-center font-medium text-sm text-gray-700 border border-[#cdd0d5]"
        >
          {actionText}
        </button>
      </div>
    </div>
  );
};

export default SearchFromSourcesHeader;
